import {Dispatch, SetStateAction, useCallback, useState} from 'react';

import {isNotRegisteredCodeError} from '@/consts/icc/iccResponseErrorTypes';
import {OTP_TYPE_CODE, OtpTypeCode} from '@/consts/loginTypes';
import useExternalLink from '@/hooks/useExternalLink';
import appService from '@/services/appService';
import authService from '@/services/auth/authService';
import appContent from '@/services/content/appContent.typed';
import log from '@/services/logger/log';
import pushNotificationService from '@/services/pushNotification/pushNotificationService';
import {hideLoader, showLoader} from '@/state/slices/loadersSlice';
import {dispatch} from '@/state/store';
import type {InlineNotificationModel} from '@/types/aem/inlineNotificationModel';

export interface OtpIdentifierSubmitParams {
    identifier: string;
    phoneCountryCode?: string;
    showFullscreenLoader?: boolean;
}

interface OtpCodeCompleteParams {
    code: string;
}

export type OtpLoginErrorDetails = InlineNotificationModel | null;

interface UseOtpLoginHandlersParams {
    otpChannel: OtpTypeCode;
    goToOtpCodeStep: () => void;
    resetOtpFlow: () => void;
    setLoginError: Dispatch<SetStateAction<OtpLoginErrorDetails>>;
}

interface UseOtpLoginHandlersReturn {
    handleOtpIdentifierSubmit: (params: OtpIdentifierSubmitParams) => Promise<void>;
    handleOtpCodeComplete: (params: OtpCodeCompleteParams) => Promise<void>;
    handleOtpBack: () => void;
}

const useOtpLoginHandlers = ({
    otpChannel,
    goToOtpCodeStep,
    resetOtpFlow,
    setLoginError,
}: UseOtpLoginHandlersParams): UseOtpLoginHandlersReturn => {
    const [otpToken, setOtpToken] = useState<string | undefined>(undefined);

    const {
        requestOtpNotRegisteredErrorEmail,
        requestOtpNotRegisteredErrorPhone,
        requestOtpGeneralErrorEmail,
        requestOtpGeneralErrorPhone,
    } = appContent.getOtpContent();

    const notRegisteredPhoneExternalLink = requestOtpNotRegisteredErrorPhone?.actionButton?.externalLink;
    const notRegisteredEmailExternalLink = requestOtpNotRegisteredErrorEmail?.actionButton?.externalLink;

    // @ts-ignore external link object is compatible with useExternalLink params
    const {redirect: redirectToNotRegisteredPhone} = useExternalLink({
        ...notRegisteredPhoneExternalLink,
    });

    // @ts-ignore external link object is compatible with useExternalLink params
    const {redirect: redirectToNotRegisteredEmail} = useExternalLink({
        ...notRegisteredEmailExternalLink,
    });

    const handleOtpIdentifierSubmit = useCallback(
        async ({
            identifier,
            phoneCountryCode,
            showFullscreenLoader = true,
        }: OtpIdentifierSubmitParams): Promise<void> => {
            if (showFullscreenLoader) {
                // @ts-ignore
                dispatch(showLoader());
            }
            setLoginError(null);

            const isPhoneChannel = otpChannel === OTP_TYPE_CODE.PHONE;

            try {
                let requestPromise;

                if (isPhoneChannel) {
                    requestPromise = authService.requestOtpSmsCode({phone: identifier, countryCode: phoneCountryCode});
                } else {
                    requestPromise = authService.requestOtpEmailCode({email: identifier});
                }
                const {
                    data: {Token: token},
                } = await requestPromise;

                setOtpToken(token);

                log.info(`LoginPageV2: otpIdentifierSubmit successfully for otpChannel: ${otpChannel}`);
                goToOtpCodeStep();
            } catch (e: any) {
                const isNotRegisteredError = isNotRegisteredCodeError(e);
                const errorDetails = isPhoneChannel
                    ? isNotRegisteredError
                        ? requestOtpNotRegisteredErrorPhone
                        : requestOtpGeneralErrorPhone
                    : isNotRegisteredError
                      ? requestOtpNotRegisteredErrorEmail
                      : requestOtpGeneralErrorEmail;

                setLoginError(errorDetails);

                log.error(`LoginPageV2: otpIdentifierSubmit for otpChannel: ${otpChannel}, error: ${e}`);

                if (isNotRegisteredError) {
                    if (isPhoneChannel) {
                        redirectToNotRegisteredPhone?.();
                    } else {
                        redirectToNotRegisteredEmail?.();
                    }
                }
            }

            // @ts-ignore
            dispatch(hideLoader());
        },
        [goToOtpCodeStep, otpChannel, redirectToNotRegisteredEmail, redirectToNotRegisteredPhone, setLoginError]
    );

    const handleOtpCodeComplete = useCallback(
        async ({code}: OtpCodeCompleteParams): Promise<void> => {
            // @ts-ignore
            dispatch(showLoader());

            try {
                if (!otpToken) {
                    log.info('LoginPageV2: otpCodeComplete, OTP token is missing');
                    throw new Error('OTP token is missing');
                }

                await authService.verifyOtpCode({
                    code,
                    token: otpToken,
                });

                log.info('LoginPageV2: OTP code verified successfully');

                await appService.fetchConsumerData();
                await pushNotificationService.checkSubscriptionInfoForNewUser();

                return;
            } catch (e) {
                log.error(`LoginPageV2: otpCodeComplete, ${e}`);
                throw e;
            } finally {
                // @ts-ignore
                dispatch(hideLoader());
            }
        },
        [otpToken]
    );

    const handleOtpBack = useCallback((): void => {
        resetOtpFlow();
    }, [resetOtpFlow]);

    return {
        handleOtpIdentifierSubmit,
        handleOtpCodeComplete,
        handleOtpBack,
    };
};

export default useOtpLoginHandlers;
